import { QuestionPromptCard, type QuestionPictureInfo } from "@/components/QuestionPrompt";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";

// Mirror of backend/api/candidate.py `_MAX_FREE_TEXT_LEN` — the server 422s
// past it, and a 422 is terminal for the save queue, so the textarea stops
// first.
export const FREE_TEXT_MAX = 20_000;

interface OpenEndedProps {
  promptMd: string;
  /** EP-19: the picture under the prompt, if the question has one. */
  picture?: QuestionPictureInfo | null;
  value: string;
  onChange: (value: string) => void;
}

// EF-10 — the candidate-facing open-ended question: the prompt and one plain
// textarea. Same skeleton as MCQ.tsx; there are no options on the wire and
// nothing is graded in the browser — a recruiter reads and scores the text by
// hand. Pastes into it are recorded by the document-level anti-cheat listener
// like everywhere else; a written answer is still the candidate's own work.
export function OpenEnded({ promptMd, picture, value, onChange }: OpenEndedProps) {
  const remaining = FREE_TEXT_MAX - value.length;
  return (
    <div className="mx-auto max-w-3xl space-y-6 p-6">
      <QuestionPromptCard promptMd={promptMd} picture={picture} />

      <Card>
        <CardHeader>
          <CardTitle>Your answer</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          <Label htmlFor="open-ended-answer" className="sr-only">
            Your answer
          </Label>
          <textarea
            id="open-ended-answer"
            value={value}
            onChange={(e) => onChange(e.target.value.slice(0, FREE_TEXT_MAX))}
            rows={10}
            maxLength={FREE_TEXT_MAX}
            spellCheck
            placeholder="Write your answer here."
            className="flex w-full resize-y rounded-md border border-input bg-transparent px-3 py-2 text-base leading-relaxed shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
          />
          <p className="text-xs text-muted-foreground">
            A recruiter reads and scores this answer by hand.
            {remaining < 1_000 && ` ${remaining} characters left.`}
          </p>
        </CardContent>
      </Card>
    </div>
  );
}

export default OpenEnded;
